import React, { useState } from 'react';
import { Box, Button, Paper, Typography, Alert, IconButton } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RichTextContent from '../../common/RichTextContent';
import { shuffle } from '../../../utils/shuffle';

interface TimelinePlayerProps {
    lesson: any;
    onComplete: () => void;
    onProgressUpdate?: (lessonId: number, progressData: {
        status?: string;
        progressPercentage?: number;
        score?: string;
        timeSpentSeconds?: number;
        suspendData?: any;
    }) => Promise<boolean>;
}

const TimelinePlayer: React.FC<TimelinePlayerProps> = ({ lesson, onComplete, onProgressUpdate }) => {
    const content = lesson.content || {};
    const { instruction, events = [], learningContent = '' } = content;

    const eventKey = events.map((event: any) => `${event.id}:${event.title}`).join('|');

    const [order, setOrder] = useState(() => ({ key: eventKey, events: shuffle<any>(events) }));
    const [checked, setChecked] = useState(false);

    if (order.key !== eventKey) {
        setOrder({ key: eventKey, events: shuffle<any>(events) });
        setChecked(false);
    }

    const sortedEvents = order.events;
    const minScore = lesson.minScore || 70;

    const moveEvent = (index: number, direction: 'up' | 'down') => {
        const next = [...sortedEvents];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
        setOrder({ key: eventKey, events: next });
    };

    const isAtCorrectPosition = (event: any, index: number) => event.position === index + 1;

    const correctCount = sortedEvents.filter(isAtCorrectPosition).length;
    const score = sortedEvents.length === 0
        ? 100
        : Math.round((correctCount / sortedEvents.length) * 100);
    const passed = score >= minScore;

    const handleComplete = async () => {
        if (onProgressUpdate) {
            await onProgressUpdate(lesson.id, {
                score: String(score),
                status: 'completed',
                progressPercentage: 100,
            });
        }
        onComplete();
    };

    return (
        <Box>
            {learningContent && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <RichTextContent html={learningContent} />
                </Paper>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {instruction || 'Ordnen Sie die Ereignisse chronologisch'}
                </Typography>

                <Alert severity="info" sx={{ mb: 2 }}>
                    Bringen Sie die Ereignisse in die richtige zeitliche Reihenfolge (vom frühesten zum spätesten)
                </Alert>

                {sortedEvents.map((event: any, index: number) => {
                    const correct = isAtCorrectPosition(event, index);

                    return (
                        <Box key={event.id} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                            <Typography sx={{ width: 30, mt: 2 }}>{index + 1}.</Typography>
                            <Paper
                                sx={{
                                    p: 2,
                                    flex: 1,
                                    ...(checked && {
                                        border: 2,
                                        borderColor: correct ? 'success.main' : 'error.main',
                                    }),
                                }}
                            >
                                {checked && (
                                    <Typography variant="subtitle2" color="primary">
                                        {event.date}
                                    </Typography>
                                )}
                                <Typography variant="body1">{event.title}</Typography>
                                {event.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {event.description}
                                    </Typography>
                                )}
                                {checked && !correct && (
                                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                        {`Richtige Position: ${event.position}`}
                                    </Typography>
                                )}
                            </Paper>
                            {checked && (correct
                                ? <CheckCircleIcon color="success" titleAccess="Richtig einsortiert" sx={{ mt: 2 }} />
                                : <CancelIcon color="error" titleAccess="Falsch einsortiert" sx={{ mt: 2 }} />
                            )}
                            <Box sx={{ mt: 1 }}>
                                <IconButton
                                    size="small"
                                    aria-label="Früher"
                                    onClick={() => moveEvent(index, 'up')}
                                    disabled={index === 0}
                                >
                                    <ArrowUpward />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    aria-label="Später"
                                    onClick={() => moveEvent(index, 'down')}
                                    disabled={index === sortedEvents.length - 1}
                                >
                                    <ArrowDownward />
                                </IconButton>
                            </Box>
                        </Box>
                    );
                })}

                {checked && (
                    <Alert severity={passed ? 'success' : 'warning'} sx={{ mt: 3 }}>
                        {passed
                            ? `Ergebnis: ${score}% — bestanden.`
                            : `Ergebnis: ${score}% (mindestens ${minScore}% nötig). Grün markierte Ereignisse stehen richtig, die rot markierten verschieben Sie an die angezeigte Position.`}
                    </Alert>
                )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                {!checked ? (
                    <Button variant="contained" onClick={() => setChecked(true)}>Absenden</Button>
                ) : (
                    <Button variant="contained" onClick={handleComplete} disabled={!passed}>
                        Bestätigen
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default TimelinePlayer;
